import useTheme from "../../../../common/hooks/useTheme";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import MetricCard from "../score/MetricCard";
import InfoIcon from "../../svg/InfoIcon";
import { NavLink } from "react-router-dom";
import BiomarkerCard from "../score/BiomarkerCard";
import React, { useState } from "react";
import DraggableCardsModal from "./DraggableCardsModal";
import {
  biomarkerDataType,
  metricDataType,
} from "../../../../providers/dataPlatformReportTypes";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import { useAppStore } from "../../../../providers/AppStore";

const OverviewMetricsWidget = ({
  data,
  title,
  type,
  footerMessage,
}: {
  data: metricDataType[] | biomarkerDataType[];
  title: string;
  type: string;
  footerMessage: string;
}) => {
  const theme = useTheme();
  const { AuthIsSimpleUser } = useAuth();
  const {
    ASSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();
  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );
  const [openModal, setOpenModal] = useState(false);
  const handleOpen = () => {
    if (isDemoReport) {
      return;
    }
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  return (
    <div style={styles.container(theme)}>
      <DraggableCardsModal
        data={data}
        type={type}
        openModal={openModal}
        handleClose={handleClose}
        title={title}
        setOpenSnackBar={(value: boolean) => {
          if (value) {
            ASSetSnackBarMessage({
              title: "Hold up! You have unsaved changes",
              message: "If you leave, your changes will be lost.",
            });
            ASSetTypeSnackBar("warning");
            ASSetSnackBarActions({
              onConfirm: () => {
                setOpenModal(false);
              },
              onCancel: () => {
                // Just close the snackbar, keep the modal open
              },
            });
            ASSetOpenSnackBar(true);
          }
        }}
      />
      <div style={styles.titleContainer}>
        <div style={styles.title(theme)}>{title}</div>
        <div
          style={styles.textButton(theme, isDemoReport)}
          onClick={handleOpen}
        >
          customize
        </div>
      </div>
      <div style={styles.cardContainer}>
        {type === "metric" &&
          (data as metricDataType[])
            .filter((el) => el.selected)
            .map((item, index: number) => (
              <MetricCard
                item={item}
                key={index}
                categories={reportData.scoreCategories}
                withShadow={false}
                isClickable={false}
              />
            ))}
        {type === "biomarker" &&
          (data as biomarkerDataType[])
            .filter((el) => el.selected)
            .map((el, index: number) => (
              <BiomarkerCard
                item={el}
                key={index}
                index={index}
                isClickable={false}
                type={"blood"}
              />
            ))}
      </div>
      <div style={styles.footerContainer}>
        <InfoIcon color={theme.colors_ds.NEUTRAL_ICON_WEAK} size={12} />
        <div style={styles.footerMessage(theme)}>
          {`${footerMessage} `}
          <NavLink
            to={
              AuthIsSimpleUser
                ? "/platform/score"
                : `/client/score?id=${
                    ASSelectedClient ? ASSelectedClient.id : ""
                  }`
            }
            style={{
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
              cursor: "pointer",
            }}
          >
            here
          </NavLink>
          .
        </div>
      </div>
    </div>
  );
};

export default OverviewMetricsWidget;

const styles = {
  container: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    marginTop: "32px",
    padding: "8px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  titleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
    gap: "24px",
  },
  title: (theme: Theme) => ({
    fontSize: "14px",
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  textButton: (theme: Theme, isDemo: boolean) => ({
    fontSize: "12px",
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: !isDemo ? "pointer" : "",
  }),
  cardContainer: {
    display: "grid",
    rowGap: "8px",
    columnGap: "8px",
    // `minmax(0, 1fr)` instead of plain `1fr` — without it, grid items
    // default to `min-width: auto` and the column with the longer card
    // content (e.g. a biomarker name that fits on one line) expands wider
    // than the column with shorter content, breaking equal-width layout.
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  },
  footerContainer: {
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  footerMessage: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "16px",
  }),
};
